// Per-call OpenAI usage report: date-range filter, totals, paged table and CSV
// export. Used by Billing (scope="merchant") and Admin > Usage (scope="admin").
// Reads the OpenAIUsageLog collection — one row per API run: timestamp,
// input, output and total tokens.
import React, { useCallback, useEffect, useState } from "react";
import { fetchUsageReport, downloadUsageCsv } from "../utils/usageReportApi";

const PAGE_SIZE = 25;

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const monthRange = (offset) => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const end = offset === 0 ? now : new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  return { from: ymd(start), to: ymd(end) };
};

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const FEATURE_LABELS = {
  "image-creation.menu-edit": "Image creation",
  "image-creation.item-image": "Item image",
  "image-creation.prompt-image": "Prompt image",
  "base-template.background": "Template background",
  "image-creation.item-enhance": "Item enhance",
  "menu-data-extraction": "Menu extraction",
  "image-extraction": "Image extraction",
};

const inputCls = "border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-gray-900 bg-white";
const btnCls =
  "px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-200 text-gray-700 bg-white disabled:opacity-50 touch-manipulation";
// The dark Export button must NOT reuse btnCls: the app's stylesheet also carries Bootstrap's `.bg-white { ... !important }`,
// which beats even Tailwind's `!bg-gray-900`, leaving white text on a white button (label invisible).
const exportBtnCls =
  "px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-900 text-white bg-gray-900 disabled:opacity-50 touch-manipulation ml-auto";

const UsageReport = ({ scope, customerId = "", showCustomer = false }) => {
  const initial = monthRange(0);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [applied, setApplied] = useState(initial); // filters the table/export currently use
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await fetchUsageReport(scope, { ...applied, customerId, page, pageSize: PAGE_SIZE }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [scope, applied, customerId, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Changing the customer filter (admin) restarts from page 1.
  useEffect(() => {
    setPage(1);
  }, [customerId]);

  const invalidRange = !!from && !!to && from > to;

  const apply = (range) => {
    setPage(1);
    setApplied(range);
  };

  const preset = (offset) => {
    const r = monthRange(offset);
    setFrom(r.from);
    setTo(r.to);
    apply(r);
  };

  const onExport = async () => {
    setExporting(true);
    setError("");
    try {
      await downloadUsageCsv(scope, { ...applied, customerId });
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const totals = data?.totals;
  const rows = data?.rows || [];
  const lastPage = Math.max(1, Math.ceil((totals?.calls || 0) / PAGE_SIZE));

  return (
    <div className="w-full min-w-0">
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <label className="text-xs text-gray-500 flex flex-col gap-1">
          From
          <input type="date" className={inputCls} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="text-xs text-gray-500 flex flex-col gap-1">
          To
          <input type="date" className={inputCls} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button type="button" className={btnCls} disabled={invalidRange || loading} onClick={() => apply({ from, to })}>
          Apply
        </button>
        <button type="button" className={btnCls} onClick={() => preset(0)}>This month</button>
        <button type="button" className={btnCls} onClick={() => preset(-1)}>Last month</button>
        <button
          type="button"
          className={exportBtnCls}
          disabled={exporting || loading || (totals?.calls || 0) === 0}
          onClick={onExport}
        >
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {invalidRange && <p className="text-sm text-red-500 mb-2">"From" must be on or before "To".</p>}
      {error && <p className="text-sm text-red-500 mb-2">{error}</p>}

      {totals && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {[
            ["API runs", totals.calls],
            ["Input tokens", totals.inputTokens],
            ["Output tokens", totals.outputTokens],
            ["Total tokens", totals.totalTokens],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-[11px] text-gray-500">{label}</p>
              <p className="text-base font-bold text-gray-900">{fmt(value)}</p>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs">
            <tr>
              <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">Timestamp</th>
              {showCustomer && <th className="text-left font-semibold px-3 py-2">Customer</th>}
              <th className="text-left font-semibold px-3 py-2">Feature</th>
              <th className="text-left font-semibold px-3 py-2">Status</th>
              <th className="text-right font-semibold px-3 py-2">Input</th>
              <th className="text-right font-semibold px-3 py-2">Output</th>
              <th className="text-right font-semibold px-3 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={showCustomer ? 7 : 6} className="px-3 py-4 text-gray-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={showCustomer ? 7 : 6} className="px-3 py-4 text-gray-500">No API runs in this date range.</td></tr>
            )}
            {!loading &&
              rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-900">{fmtTime(r.timestamp)}</td>
                  {showCustomer && <td className="px-3 py-2 whitespace-nowrap">{r.customerId || "—"}</td>}
                  <td className="px-3 py-2 whitespace-nowrap">{FEATURE_LABELS[r.feature] || r.feature || "—"}</td>
                  <td className={`px-3 py-2 ${r.status === "error" ? "text-red-500" : "text-gray-700"}`}>{r.status || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.inputTokens)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(r.outputTokens)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(r.totalTokens)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {lastPage > 1 && (
        <div className="flex items-center justify-between mt-3">
          <button type="button" className={btnCls} disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span className="text-xs text-gray-500">Page {page} of {lastPage}</span>
          <button type="button" className={btnCls} disabled={page >= lastPage || loading} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
};

export default UsageReport;
